import React, { Component } from 'react';
import Modal from '../../../web-common/components/Modal';
import AccountService from '../../../services/AccountService';
import '../../../web-common/styles/theme-button.scss';
import TrialCreationOption from '../../../web-common/models/accounts/TrialCreationOption';
import Spinner from '../../../web-common/components/Spinner';
import PageError from '../../../web-common/components/PageError';
import PublicApiError from '../../../web-common/models/errors/PublicApiError';
import './styles.scss';

interface AddTrialModalProps {
  onCloseRequested: () => void;
  onTrialCreated: () => void;
  isAdmin: boolean;
  emailVerified: boolean;
  initialSelectedProductId?: string;
}

export default class AddTrialModal extends Component<AddTrialModalProps> {

  state = {
    trialCreationOptions: undefined as TrialCreationOption[] | undefined,
    selectedOption: undefined as TrialCreationOption|undefined,
    submissionInProgress: false,
    error: '',
    estimatedTimeRemaining: undefined as string|undefined
  };

  async componentDidMount() {

    const trialCreationOptions = await AccountService.getTrialCreationOptions();
    const selectedOption = trialCreationOptions.find(o => o.productId === this.props.initialSelectedProductId);
    this.setState({
      trialCreationOptions,
      selectedOption
    });
  }

  render() {

    return (
      <Modal className="add-trial-modal" onCloseRequested={this.state.submissionInProgress ? undefined : this.props.onCloseRequested}>
        {this._renderContent()}
      </Modal>
    )
  }

  private _handleSubmitButtonClicked = async () => {

    this.setState({ submissionInProgress: true });
    const { productId } = this.state.selectedOption!;
    try {
      const result = await AccountService.createTrial(productId);
      if (result.createdAsyncWithEstimatedWaitTime) {
        let estimatedTimeRemaining = result.createdAsyncWithEstimatedWaitTime;
        this.setState({ estimatedTimeRemaining: `${estimatedTimeRemaining} secs` });
        const intervalId = setInterval(() => {
          if (--estimatedTimeRemaining <= 0) {
            clearInterval(intervalId);
            this.setState({ estimatedTimeRemaining: 'any second now' })
          } else {
            this.setState({ estimatedTimeRemaining: `${estimatedTimeRemaining} secs` })
          }
        }, 1000);
        await AccountService.waitUntilTrialFileIsReady(result.id);
        clearInterval(intervalId);
      }
      this.setState({ submissionInProgress: false, estimatedTimeRemaining: undefined });
      this.props.onTrialCreated();
    } catch (error) {
      const errorMessage = (error instanceof PublicApiError && error.userMessage) || `An unexpected error occurred: ${error}`;
      this.setState({
        submissionInProgress: false,
        error: errorMessage
      });
    }
  };

  private _renderContent() {

    if (!this.props.isAdmin) {
      return (
        <div className="non-admin">
          <h2>Please contact your account admin</h2>
          <p>
            Sorry, but only account administrators can start trials. Please contact your
            account admin to request a trial.
          </p>
        </div>
      );
    }
    if (!this.props.emailVerified) {
      return (
        <div>
          <h2>To create a trial, verify your email first</h2>
          <p>
            Before you can create a trial of 3D WebView, you must verify your email address. Click the link in the verification email that we sent you and then click below to proceed:
            <div className="email-verified-link">
              <button onClick={this._handleEmailVerifiedButtonClick} className="theme-button">I verified my email</button>
            </div>
          </p>
        </div>
      );
    }
    if (this.state.error) {
      return <PageError className="trials-error" error={this.state.error} />;
    }
    if (!this.state.trialCreationOptions || this.state.submissionInProgress) {
      return (
        <div>
          {this.state.submissionInProgress && <h2>Creating trial...</h2>}
          {this.state.estimatedTimeRemaining && <p className="estimated-time">Estimated time remaining: {this.state.estimatedTimeRemaining}</p>}
          <Spinner className="spinner"/>
        </div>
      );
    }

    const { selectedOption } = this.state;
    return (
      <div>
        <h2>Start a free trial</h2>
        <div>
          <select value={selectedOption && selectedOption.productId} onChange={this._handleValueChanged}>
            <option>Select a product</option>
            {this.state.trialCreationOptions.map(this._renderTrialCreationOption)}
          </select>
          {this._renderOptionDescriptionAndButton()}
        </div>
      </div>
    )
  }

  private _handleEmailVerifiedButtonClick = () => location.reload();

  private _handleValueChanged = event => {

    const selectedOption = this.state.trialCreationOptions!.find(o => o.productId === event.target.value);
    this.setState({ selectedOption });
  };

  private _renderOptionDescriptionAndButton() {

    const { selectedOption } = this.state;
    if (!selectedOption) {
      return;
    }
    return (
      <div>
        <p className={selectedOption.available ? '' : 'unavailable'} dangerouslySetInnerHTML={{__html: selectedOption.descriptionHtml}} />
        {selectedOption.userWarningHtml && <p className="warning" dangerouslySetInnerHTML={{__html: selectedOption.userWarningHtml}}/>}
        {selectedOption.available && <button className="theme-button" onClick={this._handleSubmitButtonClicked}>Start free trial</button>}
      </div>
    );
  }

  private _renderTrialCreationOption = (option: TrialCreationOption) => {

    return <option key={option.productId} value={option.productId}>{option.name}</option>;
  }
}
